import React from "react";
import cx from "classnames";

import styles from "./CartLineItem.module.scss";
import LineItemVariant from "./LineItemVariant";
import LineItemBadge from "./LineItemBadge";

import { strWithSuperR } from "../../../shared/utility";

// Shopify CDN images arrive with a `?v=` cache buster, so the width parameter
// tacks onto the query string that's already there. Bundled assets — the promo
// option images on the stand-in line items — have no query string and are
// already the right size, so they're left alone.
const imageSource = (lineItem) => {
  if (!lineItem.product?.image) {
    return require("assets/images/imagePlaceholder.png").default;
  }

  const url = lineItem.variant?.image?.url ?? lineItem.product.image.url;

  return url.includes("?") ? `${url}&width=300` : url;
};

/**
 * `subtitle` and `variantClassName` are both for the confirmation page, which
 * draws the same line item but adds a delivery-frequency line and greys the
 * variant back. They're optional so the checkout renders exactly as before.
 */
const CartLineItem = ({ className, lineItem, subtitle, variantClassName }) => {
  const isFree = lineItem.subtotal.amount === "0.00";

  return (
    <div
      className={cx(
        "gkit-d-flex gkit-flex-row gkit-items-start",
        styles.container,
        className
      )}
    >
      {/* Product Image */}
      <div className={styles.imgContainer}>
        <img alt={lineItem.product?.title} src={imageSource(lineItem)} />
        <div className={styles.quantityContainer}>
          <p className={cx("gkit-p-13 gkit-bold gkit-m-0", styles.quantityText)}>
            {lineItem.quantity}
          </p>
        </div>
      </div>

      {/* Product Meta */}
      <div>
        <div className={cx("gkit-mb-2", styles.badgeContainer)}>
          {/* Product type badge */}
          {lineItem.type && <LineItemBadge type={lineItem.type} />}
        </div>

        {/* Product Title */}
        <p className="gkit-p-17 gkit-mobile-15 gkit-bold gkit-my-0">
          {lineItem.product?.title ? strWithSuperR(lineItem.product.title) : null}
        </p>

        {/* Product Variant */}
        {lineItem.variant && (
          <LineItemVariant
            variant={lineItem.variant}
            className={variantClassName}
          />
        )}

        {/* Delivery frequency, e.g. "Every 4 weeks" */}
        {subtitle && (
          <p className="gkit-p-15 gkit-mobile-13 gkit-mt-1 gkit-mb-0">
            {subtitle}
          </p>
        )}

        {/* Product Price */}
        <p className="gkit-mb-0 gkit-mt-0 gkit-mt-md-2">
          {lineItem.discount.amount !== "0.00" && (
            <span className="gkit-p-13 gkit-light gkit-d-inline gkit-me-1">
              <del>{lineItem.subtotal.formatted}</del>
            </span>
          )}
          <span
            className={cx(
              "gkit-p-20 gkit-mobile-15 gkit-medium gkit-d-inline",
              isFree && styles.freePrice,
            )}
          >
            {isFree ? "FREE" : lineItem.total.formatted}
          </span>
        </p>
      </div>
    </div>
  );
};

export default CartLineItem;
